import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const CartContext = createContext(null);

// P0 canonical cart schema. Every cart line carries BOTH:
//   • Legacy display fields (name, price, kind, meta) — used by the cart
//     drawer, checkout summary and admin views. NEVER submitted as
//     authoritative to /api/orders.
//   • Canonical LineItemConfig fields (item_code, service_slug,
//     quantity, selected_options) — the ONLY fields the checkout
//     submits. The backend resolves these against the pricing authority
//     and computes the real subtotal server-side.
//
// If a line is missing item_code AND item_id, it is refused at add time
// (it would be rejected at checkout by /api/orders anyway). This is the
// "hard binary" the P0 spec requires: server-resolvable, or not at all.
const CART_SCHEMA_VERSION = 2;
const STORAGE_KEY = "urdesigns_cart_v2";
const LEGACY_KEYS = ["urdesigns_cart_v1"];

const loadInitial = () => {
  try {
    let purged = false;
    LEGACY_KEYS.forEach((k) => {
      if (localStorage.getItem(k)) {
        localStorage.removeItem(k);
        purged = true;
      }
    });
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], purged };
    const parsed = JSON.parse(raw);
    if (parsed && parsed.__schema === CART_SCHEMA_VERSION && Array.isArray(parsed.items)) {
      return { items: parsed.items, purged };
    }
    localStorage.removeItem(STORAGE_KEY);
    return { items: [], purged: true };
  } catch {
    return { items: [], purged: false };
  }
};

export const CartProvider = ({ children }) => {
  const [initial] = useState(loadInitial);
  const [items, setItems] = useState(initial.items);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (initial.purged) {
      toast.info(
        "We've updated our ordering system. Please review and rebuild your cart before checkout.",
        { duration: 6000, id: "cart-purged-p0" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ __schema: CART_SCHEMA_VERSION, items }),
      );
    } catch {}
  }, [items]);

  const addItem = (item) => {
    // Enforce server-resolvable identity at add time — refuse silently
    // is worse than telling the customer here. An unidentified line
    // would be blocked at /api/orders regardless.
    if (!item || (!item.item_code && !item.item_id)) {
      toast.error("Could not add to cart — item is missing identity");
      return;
    }
    const item_id =
      item.item_id || `${item.kind || "service"}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    trackEvent("cart_add_line", { item_name: item.name, value: item.price });
    setItems((prev) => [...prev, { qty: 1, ...item, item_id }]);
    setOpen(true);
  };

  const removeItem = (item_id) =>
    setItems((prev) => prev.filter((i) => i.item_id !== item_id));

  const updateQty = (item_id, qty) => {
    const next = Math.max(1, qty);
    setItems((prev) =>
      prev.map((i) =>
        i.item_id === item_id
          ? {
              ...i,
              // Keep both fields in lockstep — `qty` drives the cart-UI
              // subtotal, `quantity` is the canonical field submitted to
              // /api/orders. Diverging these makes the customer see one
              // price and pay a different one.
              qty: next,
              quantity: next,
            }
          : i,
      ),
    );
  };

  const clear = () => setItems([]);

  // Display-only subtotal shown in the cart UI. NEVER submitted to
  // /api/orders — the backend re-computes subtotal from the resolved
  // canonical lines.
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + Number(i.price) * Number(i.qty || 1), 0),
    [items]
  );
  // Pass 3A — recurring commitment (Website Care) aggregated across
  // cart items. All lines sharing a common non-empty label are
  // summed; empty labels don't contribute. Display-only, exactly
  // like `subtotal` — server is authoritative on the persisted
  // order.
  const { recurringSubtotal, recurringLabel } = useMemo(() => {
    let amount = 0;
    let label = "";
    for (const it of items) {
      const p = Number(it.recurring_price || 0);
      const l = (it.recurring_label || "").trim();
      if (p > 0 && l) {
        amount += p;
        if (!label) label = l;
      }
    }
    return {
      recurringSubtotal: Math.round(amount * 100) / 100,
      recurringLabel: label,
    };
  }, [items]);
  const count = useMemo(
    () => items.reduce((n, i) => n + Number(i.qty || 1), 0),
    [items]
  );

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    clear,
    open,
    setOpen,
    subtotal,
    recurringSubtotal,
    recurringLabel,
    count,
    schemaVersion: CART_SCHEMA_VERSION,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

// Extract the canonical LineItemConfig payload for POST /api/orders from
// a cart line. All commercial values are stripped — the backend resolves
// them against the pricing authority.
export const toLineItemConfig = (it) => ({
  kind: it.kind || "service",
  service_slug: it.service_slug || it.meta?.slug || it.meta?.service_slug || "",
  line_type: it.line_type || "",
  item_code: it.item_code || "",
  item_id: it.item_id || it.meta?.item_id || "",
  quantity: Number(it.quantity ?? it.qty ?? 1),
  selected_addons: Array.isArray(it.selected_addons) ? it.selected_addons : [],
  selected_options: it.selected_options || {},
  parent_service_id: it.parent_service_id || it.meta?.parent_service_id || "",
  meta: it.meta || {},
});
